(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  const initUnboxing = (root) => {
    const section = root.querySelector?.('[data-nalby-unboxing]') || (root.matches?.('[data-nalby-unboxing]') ? root : null);
    if (!section || section.dataset.nalbyReady === 'true') return;
    section.dataset.nalbyReady = 'true';

    const chapters = Array.from(section.querySelectorAll('[data-nalby-chapter]'));
    const progressDots = Array.from(section.querySelectorAll('[data-nalby-dot]'));
    let currentProgress = 0;
    let targetProgress = 0;
    let frame = 0;
    let measureFrame = 0;
    let activeIndex = -1;
    const mobileViewport = window.matchMedia('(max-width: 900px)');

    const pageWrapper = document.querySelector('.page-wrapper');
    const header = document.querySelector('[data-nalby-header]');
    const getScrollContext = () => {
      if (pageWrapper) {
        const wrapperStyle = window.getComputedStyle(pageWrapper);
        const wrapperScrolls = /(auto|scroll)/.test(wrapperStyle.overflowY)
          && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1;

        if (wrapperScrolls) {
          return {
            element: pageWrapper,
            top: pageWrapper.getBoundingClientRect().top,
            height: pageWrapper.clientHeight,
          };
        }
      }

      return { element: window, top: 0, height: window.innerHeight };
    };

    const render = () => {
      const delta = targetProgress - currentProgress;
      const epsilon = mobileViewport.matches ? 0.0015 : 0.0005;
      const smoothing = mobileViewport.matches ? 0.22 : 0.14;
      currentProgress = Math.abs(delta) < epsilon ? targetProgress : currentProgress + delta * smoothing;
      const eased = currentProgress < 0.5
        ? 2 * currentProgress * currentProgress
        : 1 - Math.pow(-2 * currentProgress + 2, 2) / 2;

      section.style.setProperty('--reveal', eased.toFixed(4));
      const index = Math.min(chapters.length - 1, Math.floor(currentProgress * chapters.length));

      if (index !== activeIndex) {
        activeIndex = index;
        chapters.forEach((chapter, chapterIndex) => chapter.classList.toggle('is-active', chapterIndex === index));
        progressDots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      }

      if (currentProgress !== targetProgress) {
        frame = requestAnimationFrame(render);
      } else {
        frame = 0;
      }
    };

    const measure = () => {
      measureFrame = 0;
      const scrollContext = getScrollContext();
      const rect = section.getBoundingClientRect();
      const travel = Math.max(1, section.offsetHeight - scrollContext.height);
      const relativeTop = rect.top - scrollContext.top;
      targetProgress = clamp(-relativeTop / travel);
      section.dataset.nalbyScrollRoot = scrollContext.element === window ? 'window' : 'page-wrapper';
      const shouldReturnHeader = targetProgress >= 0.94;
      header?.classList.toggle('is-returned', shouldReturnHeader);
      header?.setAttribute('data-nalby-returned', String(shouldReturnHeader));
      document.body.classList.toggle('nalby-header-returned', shouldReturnHeader);
      if (!frame) frame = requestAnimationFrame(render);
    };

    const queueMeasure = () => {
      if (!measureFrame) measureFrame = requestAnimationFrame(measure);
    };

    if ('IntersectionObserver' in window) {
      const motionObserver = new IntersectionObserver(([entry]) => {
        section.classList.toggle('is-motion-active', entry.isIntersecting);
      }, { rootMargin: '30% 0px' });
      motionObserver.observe(section);
    } else {
      section.classList.add('is-motion-active');
    }

    window.addEventListener('scroll', queueMeasure, { passive: true });
    pageWrapper?.addEventListener('scroll', queueMeasure, { passive: true });
    window.addEventListener('resize', queueMeasure, { passive: true });
    window.addEventListener('load', queueMeasure, { once: true });
    measure();
  };

  const initHeader = (root) => {
    const header = root.querySelector?.('[data-nalby-header]') || (root.matches?.('[data-nalby-header]') ? root : null);
    if (!header || header.dataset.nalbyReady === 'true') return;
    header.dataset.nalbyReady = 'true';

    const menu = header.querySelector('[data-nalby-menu]');
    const toggle = header.querySelector('[data-nalby-menu-toggle]');
    const closeButtons = Array.from(header.querySelectorAll('[data-nalby-menu-close]'));
    if (!menu || !toggle) return;

    const innerPage = document.body.classList.contains('nalby-template-inner');
    const pageWrapper = document.querySelector('.page-wrapper');
    let headerFrame = 0;
    const updateHeaderScrollState = () => {
      headerFrame = 0;
      if (!innerPage) return;
      const wrapperStyle = pageWrapper ? window.getComputedStyle(pageWrapper) : null;
      const wrapperScrolls = pageWrapper && /(auto|scroll)/.test(wrapperStyle?.overflowY || '')
        && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1;
      const scrollTop = wrapperScrolls ? pageWrapper.scrollTop : window.scrollY;
      header.classList.toggle('is-scrolled', scrollTop > 18);
    };
    const queueHeaderScrollState = () => {
      if (!headerFrame) headerFrame = requestAnimationFrame(updateHeaderScrollState);
    };

    window.addEventListener('scroll', queueHeaderScrollState, { passive: true });
    pageWrapper?.addEventListener('scroll', queueHeaderScrollState, { passive: true });
    updateHeaderScrollState();

    const setOpen = (open) => {
      menu.classList.toggle('is-open', open);
      menu.setAttribute('aria-hidden', String(!open));
      menu.toggleAttribute('inert', !open);
      toggle.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('nalby-menu-open', open);
      if (open) {
        requestAnimationFrame(() => menu.querySelector('[data-nalby-menu-close]:not(.nalby-menu__backdrop)')?.focus());
      }
    };

    toggle.addEventListener('click', () => setOpen(!menu.classList.contains('is-open')));
    closeButtons.forEach((button) => button.addEventListener('click', () => setOpen(false)));
    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) {
        setOpen(false);
        toggle.focus();
      }
      if (event.key === 'Tab' && menu.classList.contains('is-open')) {
        const focusable = Array.from(menu.querySelectorAll('a, button:not([disabled])'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    });
  };

  const initReveal = (root) => {
    const elements = Array.from(root.querySelectorAll?.('.nalby-reveal') || []);
    if (!elements.length) return;

    const mobileReveal = window.matchMedia('(max-width: 600px)').matches;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        instance.unobserve(entry.target);
      });
    }, mobileReveal
      ? { threshold: 0.01, rootMargin: '20% 0px 34% 0px' }
      : { threshold: 0.15, rootMargin: '0px 0px -7% 0px' });

    elements.forEach((element) => observer.observe(element));
  };

  const initProductGallery = (root) => {
    const galleries = Array.from(root.querySelectorAll?.('[data-nalby-gallery]') || []);
    galleries.forEach((gallery) => {
      if (gallery.dataset.nalbyReady === 'true') return;
      gallery.dataset.nalbyReady = 'true';
      const main = gallery.querySelector('[data-nalby-main-image]');
      const thumbs = Array.from(gallery.querySelectorAll('[data-nalby-thumb]'));
      if (!main || !thumbs.length) return;

      thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
        const nextSrc = thumb.dataset.src;
        const nextAlt = thumb.dataset.alt || 'NALBY Story';
        if (!nextSrc || main.src === nextSrc) return;
        thumbs.forEach((item) => item.classList.toggle('is-active', item === thumb));
        main.classList.add('is-changing');
        const preload = new Image();
        preload.onload = () => {
          main.src = nextSrc;
          main.alt = nextAlt;
          requestAnimationFrame(() => main.classList.remove('is-changing'));
        };
        preload.src = nextSrc;
      }));
    });
  };

  const initMobileBuy = (root) => {
    const bars = Array.from(root.querySelectorAll?.('[data-nalby-mobile-buy]') || []);
    bars.forEach((bar) => {
      if (bar.dataset.nalbyReady === 'true') return;
      bar.dataset.nalbyReady = 'true';

      const mobileViewport = window.matchMedia('(max-width: 900px)');
      const product = bar.closest('.nalby-product');
      const mainBuy = product?.querySelector('[data-nalby-main-buy]');
      const headerGroup = document.querySelector('#header-group');
      const pageWrapper = document.querySelector('.page-wrapper');
      let buyFrame = 0;

      const update = () => {
        buyFrame = 0;
        const headerBottom = headerGroup?.getBoundingClientRect().bottom || 0;
        const mainBuyIsPast = mainBuy
          ? mainBuy.getBoundingClientRect().bottom <= headerBottom + 1
          : false;
        const visible = mobileViewport.matches && mainBuyIsPast;
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
      };

      const queueUpdate = () => {
        if (!buyFrame) buyFrame = requestAnimationFrame(update);
      };

      window.addEventListener('scroll', queueUpdate, { passive: true });
      pageWrapper?.addEventListener('scroll', queueUpdate, { passive: true });
      window.addEventListener('resize', queueUpdate, { passive: true });
      mobileViewport.addEventListener?.('change', queueUpdate);
      update();
    });
  };

  const init = (root = document) => {
    initHeader(root);
    initUnboxing(root);
    initReveal(root);
    initProductGallery(root);
    initMobileBuy(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/*
 * IZPRATI.BG renders the office / locker results inside an open Shadow DOM.
 * Theme styles cannot cross that boundary, so add a tiny NALBY skin after the
 * component creates its shadow root. The observer is frame-throttled and only
 * scans while the delivery popup is being mounted or updated.
 */
(() => {
  const pickerSelector = '#octava-checkout-modal .octava-service-points-list';
  const styleMarker = 'data-nalby-izprati-skin';
  const pickerStyles = `
    :host {
      color: #f4f0e7;
      font-family: "Manrope", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      color-scheme: dark;
    }

    .list { gap: 8px; }

    .item {
      border: 1px solid rgba(202, 163, 73, .28);
      border-radius: 5px;
      color: #f4f0e7;
      background: #090806;
      box-shadow: inset 0 1px rgba(255, 255, 255, .02);
      transition: border-color .16s ease, background-color .16s ease, box-shadow .16s ease;
    }

    .item:hover {
      border-color: rgba(239, 212, 107, .68);
      background: #121008;
    }

    .item[data-selected="true"] {
      border-color: #efd46b;
      color: #f4f0e7;
      background: linear-gradient(105deg, rgba(202, 163, 73, .17), rgba(202, 163, 73, .03)), #0d0b07;
      box-shadow: 0 0 0 1px rgba(202, 163, 73, .18);
    }

    .radio {
      border-color: rgba(244, 240, 231, .5);
      background: #050504;
    }

    .item[data-selected="true"] .radio { border-color: #efd46b; }
    .item[data-selected="true"] .radio::after { background: #efd46b; }

    .name,
    .price { color: #f4f0e7; }

    .item[data-selected="true"] .price { color: #efd46b; }

    .address,
    .metaLine,
    .distance,
    .fallbackSortLabel,
    .paginationStatus,
    .loading,
    .empty { color: #a9a294; }

    .badge {
      border: 1px solid rgba(202, 163, 73, .23);
      color: #c8c0b0;
      background: rgba(202, 163, 73, .08);
    }

    .badge.is-prepaid-only {
      border-color: rgba(202, 163, 73, .38);
      color: #efd46b;
      background: rgba(202, 163, 73, .1);
    }

    .logo {
      height: 24px;
      max-width: 72px;
      padding: 3px 5px;
      border-radius: 3px;
      background: rgba(255, 255, 255, .94);
      box-sizing: border-box;
    }

    .thumb,
    .preview {
      border-color: rgba(202, 163, 73, .28);
      background: #090806;
    }

    .fallbackSortChoices {
      border-color: rgba(202, 163, 73, .28);
      background: #090806;
    }

    .fallbackSortChoice { color: #c8c0b0; }

    .fallbackSortChoice[aria-pressed="true"] {
      color: #050504;
      background: #efd46b;
    }

    .empty {
      border-color: rgba(202, 163, 73, .28);
      background: #090806;
    }

    .paginationButton {
      border-color: rgba(202, 163, 73, .34);
      border-radius: 4px;
      color: #f4f0e7;
      background: #11100d;
    }

    .paginationButton:hover:not(:disabled) {
      border-color: #efd46b;
      color: #efd46b;
      background: #17130b;
    }

    .paginationButton:disabled {
      color: #615c52;
      background: #090806;
    }

    .loading { border-top-color: rgba(202, 163, 73, .24); }
    .spinner { border-color: rgba(202, 163, 73, .22); border-top-color: #efd46b; }

    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after { transition: none !important; animation: none !important; }
    }
  `;

  const skinPicker = (host) => {
    const shadow = host?.shadowRoot;
    if (!shadow || shadow.querySelector(`style[${styleMarker}]`)) return;

    const style = document.createElement('style');
    style.setAttribute(styleMarker, '');
    style.textContent = pickerStyles;
    shadow.appendChild(style);
  };

  const annotateDeliveryCards = () => {
    document.querySelectorAll('#octava-checkout-modal .octava-cod-card:not(.octava-payment-card)').forEach((card) => {
      const label = card.querySelector('.octava-cod-card-label')?.textContent?.toLocaleLowerCase('bg-BG') || '';
      let kind = '';
      if (label.includes('автомат')) kind = 'locker';
      else if (label.includes('адрес')) kind = 'address';
      else if (label.includes('офис')) kind = 'office';

      if (kind) card.dataset.nalbyDeliveryKind = kind;

      const logo = card.querySelector('.octava-cod-card-logo img');
      const courier = logo?.getAttribute('alt')?.trim()
        || (label.includes('speedy') ? 'Speedy' : label.includes('econt') ? 'Econt' : '');
      if (courier) {
        card.dataset.nalbyCourier = courier;
        card.dataset.nalbyDeliveryKey = `${kind}:${courier.toLocaleLowerCase('bg-BG')}`;
      }
    });
  };

  const deliveryKindMeta = {
    office: {
      title: 'До офис',
      detailTitle: 'Изберете конкретен офис',
      detailNote: 'Потърсете по град, офис или адрес.',
    },
    address: {
      title: 'До адрес',
      detailTitle: 'Посочете адрес за доставка',
      detailNote: 'Въведете точните данни за получаване.',
    },
    locker: {
      title: 'До автомат',
      detailTitle: 'Изберете конкретен автомат',
      detailNote: 'Потърсете най-удобния автомат близо до вас.',
    },
  };

  const isSelectedDeliveryCard = (card) => card?.classList.contains('is-selected')
    || card?.querySelector('.octava-cod-card-radio')?.getAttribute('aria-checked') === 'true';

  const createWizardHeading = (step, title) => {
    const heading = document.createElement('div');
    heading.className = 'nalby-delivery-wizard-heading';

    const index = document.createElement('span');
    index.className = 'nalby-delivery-wizard-index';
    index.textContent = step;

    const text = document.createElement('h4');
    text.textContent = title;

    heading.append(index, text);
    return heading;
  };

  const buildDeliveryWizard = (section, cards) => {
    const cardsContainer = section.querySelector('.octava-cod-delivery-cards');
    let wizard = section.querySelector('.nalby-delivery-wizard');
    if (!wizard) {
      wizard = document.createElement('div');
      wizard.className = 'nalby-delivery-wizard';
      wizard.setAttribute('aria-label', 'Избор на доставка');

      wizard.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const kindButton = event.target.closest('[data-nalby-wizard-kind]');
        if (kindButton) {
          section.dataset.nalbyWizardKind = kindButton.dataset.nalbyWizardKind || '';
          syncDeliveryWizard(section);
        }
      });

      cardsContainer?.before(wizard);

      section.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        const header = event.target.closest('.octava-cod-card:not(.octava-payment-card) .octava-cod-card-header');
        if (!header || !section.contains(header)) return;

        requestAnimationFrame(() => syncDeliveryWizard(section));
        setTimeout(() => syncDeliveryWizard(section), 450);
        setTimeout(() => syncDeliveryWizard(section), 1100);
      }, true);
    }

    let details = cardsContainer?.querySelector(':scope > .nalby-delivery-details-heading');
    if (!details && cardsContainer) {
      details = document.createElement('section');
      details.className = 'nalby-delivery-details-heading';
      details.hidden = true;
      details.append(createWizardHeading('03', ''));
      const note = document.createElement('p');
      details.append(note);
      cardsContainer.append(details);
    }

    const signature = cards.map((card) => [
      card.dataset.nalbyDeliveryKey,
      card.querySelector('.octava-cod-card-price')?.textContent?.trim(),
      card.querySelector('.octava-cod-card-logo img')?.getAttribute('src'),
    ].join('|')).join('||');
    if (wizard.dataset.nalbySignature === signature) return wizard;
    wizard.dataset.nalbySignature = signature;

    const typeStep = document.createElement('section');
    typeStep.className = 'nalby-delivery-wizard-step nalby-delivery-wizard-types';
    typeStep.append(createWizardHeading('01', 'Как искате да получите пратката?'));

    const typeChoices = document.createElement('div');
    typeChoices.className = 'nalby-delivery-type-choices';
    ['office', 'address', 'locker'].forEach((kind) => {
      const matching = cards.filter((card) => card.dataset.nalbyDeliveryKind === kind);
      if (!matching.length) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nalby-delivery-type-choice';
      button.dataset.nalbyWizardKind = kind;
      button.setAttribute('aria-pressed', 'false');

      const icon = document.createElement('span');
      icon.className = 'nalby-delivery-choice-icon';
      icon.setAttribute('aria-hidden', 'true');

      const title = document.createElement('strong');
      title.textContent = deliveryKindMeta[kind].title;

      const price = document.createElement('span');
      price.className = 'nalby-delivery-choice-price';
      const priceText = matching[0].querySelector('.octava-cod-card-price')?.textContent?.trim() || '';
      price.textContent = priceText ? `от ${priceText}` : '';

      const check = document.createElement('span');
      check.className = 'nalby-delivery-choice-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';

      button.append(icon, title, price, check);
      typeChoices.append(button);
    });
    typeStep.append(typeChoices);

    const courierStep = document.createElement('section');
    courierStep.className = 'nalby-delivery-wizard-step nalby-delivery-wizard-couriers';
    courierStep.hidden = true;
    const courierHeading = createWizardHeading('02', 'Изберете куриер');

    const courierHint = document.createElement('p');
    courierHint.className = 'nalby-delivery-courier-hint';
    courierHint.textContent = 'Изберете Еконт или Speedy.';
    courierStep.append(courierHeading, courierHint);

    wizard.replaceChildren(typeStep, courierStep);
    return wizard;
  };

  const syncDeliveryWizard = (section) => {
    if (!section?.isConnected) return;

    annotateDeliveryCards();
    const cards = [...section.querySelectorAll('.octava-cod-card:not(.octava-payment-card)')]
      .filter((card) => card.dataset.nalbyDeliveryKind && card.dataset.nalbyDeliveryKey);
    if (!cards.length) return;

    const wizard = buildDeliveryWizard(section, cards);
    const selectedCard = cards.find(isSelectedDeliveryCard);
    let currentKind = section.dataset.nalbyWizardKind || selectedCard?.dataset.nalbyDeliveryKind || '';
    if (!cards.some((card) => card.dataset.nalbyDeliveryKind === currentKind)) currentKind = '';
    section.dataset.nalbyWizardKind = currentKind;

    const selectedMatchesKind = Boolean(selectedCard && selectedCard.dataset.nalbyDeliveryKind === currentKind);
    section.dataset.nalbyWizardPending = String(Boolean(currentKind && !selectedMatchesKind));
    section.classList.add('nalby-delivery-wizard-enabled');
    section.classList.toggle('nalby-delivery-wizard-pending', Boolean(currentKind && !selectedMatchesKind));
    section.classList.remove('nalby-delivery-wizard-reselecting');

    wizard.querySelectorAll('[data-nalby-wizard-kind]').forEach((button) => {
      const active = button.dataset.nalbyWizardKind === currentKind;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-pressed', String(active));
    });

    const courierStep = wizard.querySelector('.nalby-delivery-wizard-couriers');
    if (courierStep) courierStep.hidden = !currentKind;
    const courierTitle = courierStep?.querySelector('h4');
    if (courierTitle) courierTitle.textContent = 'Изберете куриер';
    const courierHint = courierStep?.querySelector('.nalby-delivery-courier-hint');
    if (courierHint) {
      courierHint.hidden = false;
      courierHint.textContent = selectedMatchesKind
        ? `Избрано: ${selectedCard.dataset.nalbyCourier || ''}. Можете да го смените директно.`
        : 'Изберете Еконт или Speedy.';
    }

    cards.forEach((card) => {
      const matchesKind = card.dataset.nalbyDeliveryKind === currentKind;
      card.classList.toggle('nalby-wizard-courier-candidate', Boolean(currentKind && matchesKind));
      card.classList.toggle('nalby-wizard-active-card', Boolean(selectedMatchesKind && card === selectedCard));
    });

    const details = section.querySelector('.nalby-delivery-details-heading');
    if (details) {
      details.hidden = !selectedMatchesKind;
      const meta = deliveryKindMeta[currentKind];
      const heading = details.querySelector('h4');
      const note = details.querySelector('p');
      if (heading) heading.textContent = meta?.detailTitle || '';
      if (note) note.textContent = meta?.detailNote || '';
    }
  };

  const initDeliveryWizard = () => {
    const modal = document.querySelector('#octava-checkout-modal');
    const section = modal?.querySelector('.octava-cod-delivery-section');
    if (!modal || !section) return;

    syncDeliveryWizard(section);

    if (modal.dataset.nalbyWizardGuard === 'true') return;
    modal.dataset.nalbyWizardGuard = 'true';
    modal.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const submit = event.target.closest('.octava-continue-btn');
      const currentSection = modal.querySelector('.octava-cod-delivery-section');
      if (!submit || !currentSection?.classList.contains('nalby-delivery-wizard-pending')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      currentSection.classList.add('nalby-delivery-wizard-attention');
      currentSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => currentSection.classList.remove('nalby-delivery-wizard-attention'), 1200);
    }, true);
  };

  const initCheckoutStickySubmit = () => {
    const modal = document.querySelector('#octava-checkout-modal');
    const submit = modal?.querySelector('.octava-continue-btn');
    if (!modal || !submit || modal.dataset.nalbyStickySubmit === 'true') return;
    modal.dataset.nalbyStickySubmit = 'true';

    const dock = document.createElement('div');
    dock.className = 'nalby-sticky-submit';
    dock.setAttribute('aria-hidden', 'true');

    const dockButton = document.createElement('button');
    dockButton.className = 'nalby-sticky-submit__button';
    dockButton.type = 'button';
    dockButton.textContent = submit.textContent?.trim() || 'Завърши поръчката';
    dock.append(dockButton);
    modal.append(dock);

    const findScrollRoot = (element) => {
      let current = element.parentElement;
      while (current && current !== modal) {
        const style = window.getComputedStyle(current);
        if (/(auto|scroll)/.test(style.overflowY) && current.scrollHeight > current.clientHeight + 1) return current;
        current = current.parentElement;
      }
      return modal;
    };

    const scrollRoot = findScrollRoot(submit);
    let frame = 0;
    const sync = () => {
      frame = 0;
      if (!submit.isConnected || !modal.isConnected) return;
      const header = modal.querySelector('.octava-checkout-header');
      const threshold = (header?.getBoundingClientRect().bottom || 0) + 8;
      const hasPassed = submit.getBoundingClientRect().bottom < threshold;
      const isDisabled = submit.matches(':disabled, [aria-disabled="true"]');
      modal.classList.toggle('nalby-sticky-submit-visible', hasPassed);
      dock.setAttribute('aria-hidden', String(!hasPassed));
      dockButton.disabled = isDisabled;
      dockButton.textContent = submit.textContent?.trim() || 'Завърши поръчката';
    };
    const queueSync = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };

    dockButton.addEventListener('click', () => {
      if (dockButton.disabled) return;
      submit.click();
    });
    scrollRoot.addEventListener('scroll', queueSync, { passive: true });
    if (scrollRoot !== modal) modal.addEventListener('scroll', queueSync, { passive: true });
    window.addEventListener('scroll', queueSync, { passive: true });
    window.addEventListener('resize', queueSync, { passive: true });
    window.visualViewport?.addEventListener('resize', queueSync, { passive: true });
    new MutationObserver(queueSync).observe(submit, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
    sync();
  };

  const validationFields = [
    { key: 'firstName', message: 'Въведете собствено име.' },
    { key: 'lastName', message: 'Въведете фамилно име.' },
    { key: 'phone', message: 'Въведете телефонен номер.' },
    { key: 'email', message: 'Въведете имейл адрес.' },
  ];

  const normalizeValidationErrors = () => {
    const modal = document.querySelector('#octava-checkout-modal');
    if (!modal) return;

    let fieldErrorCount = 0;
    validationFields.forEach(({ key, message }) => {
      const error = modal.querySelector(`.octava-error-${key}`);
      const input = modal.querySelector(`[name="${key}"]`);
      const hasError = Boolean(error?.textContent?.trim());

      if (!hasError) {
        input?.removeAttribute('aria-invalid');
        if (input?.getAttribute('aria-describedby') === `nalby-error-${key}`) {
          input.removeAttribute('aria-describedby');
        }
        return;
      }

      fieldErrorCount += 1;
      if (error.textContent.trim() !== message) error.textContent = message;
      error.id = `nalby-error-${key}`;
      error.setAttribute('role', 'alert');
      error.setAttribute('aria-live', 'polite');
      input?.setAttribute('aria-invalid', 'true');
      input?.setAttribute('aria-describedby', error.id);
    });

    const deliverySection = modal.querySelector('.octava-cod-delivery-section');
    const selectedDelivery = deliverySection?.querySelector('.octava-cod-card.is-selected');
    const rawSummary = modal.querySelector('.octava-form-errors');
    if (!rawSummary) return;

    const rawText = rawSummary.textContent || '';
    const ownsSummary = rawSummary.children.length === 1
      && rawSummary.firstElementChild?.classList.contains('nalby-validation-summary');
    const rawHasDeliveryError = /изберете (метод|опция) за доставка/i.test(rawText);
    const hasDeliveryError = ownsSummary
      ? rawSummary.dataset.nalbyDeliveryError === 'true'
      : rawHasDeliveryError;
    rawSummary.dataset.nalbyDeliveryError = String(hasDeliveryError);
    let deliveryMessage = '';
    if (hasDeliveryError) {
      const kind = selectedDelivery?.dataset.nalbyDeliveryKind;
      if (kind === 'office') deliveryMessage = 'Изберете конкретен офис.';
      else if (kind === 'locker') deliveryMessage = 'Изберете конкретен автомат.';
      else if (kind === 'address') deliveryMessage = 'Попълнете адреса за доставка.';
      else deliveryMessage = 'Изберете начин на доставка.';
    }

    const deliveryHeading = deliverySection?.querySelector('.octava-form-heading-row');
    deliverySection?.classList.toggle('nalby-has-validation-error', Boolean(deliveryMessage));
    if (deliveryHeading && deliveryMessage) {
      deliveryHeading.dataset.nalbyValidationMessage = deliveryMessage;
    } else {
      deliveryHeading?.removeAttribute('data-nalby-validation-message');
    }

    let summaryMessage = '';
    if (fieldErrorCount && deliveryMessage) {
      summaryMessage = 'Попълнете отбелязаните полета и изберете доставка.';
    } else if (fieldErrorCount) {
      summaryMessage = 'Попълнете отбелязаните полета.';
    } else {
      summaryMessage = deliveryMessage;
    }

    if (!summaryMessage) {
      rawSummary.hidden = true;
      rawSummary.removeAttribute('role');
      rawSummary.removeAttribute('aria-live');
      return;
    }

    rawSummary.hidden = false;
    rawSummary.setAttribute('role', 'alert');
    rawSummary.setAttribute('aria-live', 'polite');
    const existing = rawSummary.querySelector('.nalby-validation-summary');
    if (existing && existing.textContent === summaryMessage && rawSummary.children.length === 1) return;

    const message = document.createElement('div');
    message.className = 'nalby-validation-summary';
    message.textContent = summaryMessage;
    rawSummary.replaceChildren(message);
  };

  const normalizePaymentHeading = () => {
    const heading = document.querySelector(
      '#octava-checkout-modal .octava-cod-payment > h3'
    );
    if (heading?.textContent?.trim() === 'Начин на плащане') {
      heading.textContent = 'Метод на плащане';
    }
  };

  const scan = () => {
    annotateDeliveryCards();
    initDeliveryWizard();
    initCheckoutStickySubmit();
    normalizePaymentHeading();
    normalizeValidationErrors();
    document.querySelectorAll(pickerSelector).forEach(skinPicker);
  };
  let frame = 0;
  const queueScan = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      scan();
    });
  };

  const start = () => {
    scan();
    new MutationObserver(queueScan).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
